import axios from "axios";

/**
 * The single axios instance every API call goes through.
 *
 * `REACT_APP_API_URL` must end with a trailing slash (see `.env.example`), and
 * request paths passed to this client must be relative with NO leading slash —
 * a leading slash would discard any base path in the URL.
 *
 * Two deliberate carve-outs do NOT use this client:
 *   - `src/utils/s3Upload.ts` uses raw `fetch` for the presigned PUT, because an
 *     `Authorization` header makes S3 reject the upload.
 *   - the `x-app-version` header is attached per-call (see `src/queries/keys.ts`
 *     consumers), never globally — the API serves legacy response shapes to
 *     clients that omit it, so sending it everywhere would change the shape of
 *     every other endpoint at once.
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.REACT_APP_API_URL,
});

/**
 * A misconfigured `REACT_APP_API_URL` turns an API call into a same-origin
 * request that the SPA fallback answers with `index.html` and a 200. Several
 * endpoints guarded against that individually; this is the shared check.
 */
export const assertJsonObject = <T>(data: unknown, endpoint: string): T => {
  if (!data || typeof data !== "object") {
    throw new Error(`Unexpected non-object response from ${endpoint}`);
  }
  return data as T;
};

export const assertJsonArray = <T>(data: unknown, endpoint: string): T[] => {
  if (!Array.isArray(data)) {
    throw new Error(`Unexpected non-array response from ${endpoint}`);
  }
  return data as T[];
};
